import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { FEEDBACK_ENABLED } from '@/lib/feature-flags';
import { fetchMyFeedback, upsertFeedback } from '@/lib/supabase-queries';
import {
  FEATURE_WISH_MAX,
  FEATURE_WISH_OPTIONS,
  FEATURE_WISH_OTHER,
  RETURN_INTENT_OPTIONS,
  RETURN_INTENT_SHOWS_BLOCKER,
  type FeedbackInput,
  type ReturnIntent,
} from '@/types/feedback';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

// Composed from the standalone-page token family (see About.tsx / LoginPage.tsx).
// Selected mirrors the app's existing accent-selection idiom; unselected uses the
// subtle-surface tokens already used on these pages. No new visual language.
const chipClass = (selected: boolean) =>
  cn(
    'min-h-[48px] rounded-xl border px-4 py-3 text-sm font-medium transition-colors text-center',
    'disabled:opacity-40 disabled:pointer-events-none',
    selected
      ? 'border-accent bg-accent/15 text-white'
      : 'border-white/10 bg-white/5 text-white/70 hover:border-accent/50 hover:text-white',
  );

const inputClass =
  'bg-[#15191E] border border-white/10 text-right focus-visible:ring-[#F97116]/50';

const QuestionCard = ({
  title,
  required,
  children,
}: {
  title: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <motion.div
    variants={fadeUp}
    className="bg-[#1A1F25] border border-white/5 rounded-2xl p-5"
  >
    <h2 className="font-bold text-base mb-4 leading-relaxed">
      {title}
      {required && <span className="text-accent"> *</span>}
    </h2>
    {children}
  </motion.div>
);

const RatingScale = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) => (
  <div className="flex gap-2">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={cn(chipClass(value === n), 'flex-1 text-lg font-bold')}
        aria-pressed={value === n}
      >
        {n}
      </button>
    ))}
  </div>
);

const FeedbackPage = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Logged-out visitors: remember where to return after in-page sign-in.
  useEffect(() => {
    if (FEEDBACK_ENABLED && !loading && !user) {
      localStorage.setItem('postAuthRedirect', '/feedback');
    }
  }, [loading, user]);

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['feedback', user?.id],
    queryFn: () => fetchMyFeedback(user!.id),
    enabled: FEEDBACK_ENABLED && !!user,
  });

  const [overall, setOverall] = useState(0);
  const [clarity, setClarity] = useState(0);
  const [intent, setIntent] = useState<ReturnIntent | ''>('');
  const [blocker, setBlocker] = useState('');
  const [liked, setLiked] = useState('');
  const [improve, setImprove] = useState('');
  const [wishes, setWishes] = useState<string[]>([]);
  const [featureOther, setFeatureOther] = useState('');
  const [topImprovement, setTopImprovement] = useState('');
  const [other, setOther] = useState('');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Reset the form whenever the authenticated user changes, so one user's
  // draft can never be submitted under another user's id.
  useEffect(() => {
    setOverall(0);
    setClarity(0);
    setIntent('');
    setBlocker('');
    setLiked('');
    setImprove('');
    setWishes([]);
    setFeatureOther('');
    setTopImprovement('');
    setOther('');
    setDone(false);
    setSeededFor(null);
  }, [user?.id]);

  // Pre-fill once per user from their existing row (edit mode).
  useEffect(() => {
    if (existing && user && seededFor !== user.id) {
      setOverall(existing.overall_rating);
      setClarity(existing.scoring_clarity);
      setIntent(existing.return_intent);
      setBlocker(existing.return_blocker ?? '');
      setLiked(existing.liked_text ?? '');
      setImprove(existing.improve_text ?? '');
      setWishes(existing.feature_wishes ?? []);
      setFeatureOther(existing.feature_other ?? '');
      setTopImprovement(existing.top_improvement ?? '');
      setOther(existing.other_text ?? '');
      setSeededFor(user.id);
    }
  }, [existing, user, seededFor]);

  const mutation = useMutation({
    mutationFn: (input: FeedbackInput) => upsertFeedback(user!.id, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(['feedback', user!.id], saved);
      setDone(true);
      toast({ title: 'תודה! 🙏', description: 'המשוב נשלח בהצלחה' });
    },
    onError: () => {
      toast({
        title: 'שגיאה',
        description: 'שליחת המשוב נכשלה, נסה שוב',
        variant: 'destructive',
      });
    },
  });

  // Flag + auth gates. Route already guards the flag; this is defence in depth.
  if (!FEEDBACK_ENABLED) return <Navigate to="/" replace />;
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }
  if (!user) return <LoginPage />;

  const isEditing = !!existing;
  const showBlocker = intent !== '' && RETURN_INTENT_SHOWS_BLOCKER.includes(intent);
  const showFeatureOther = wishes.includes(FEATURE_WISH_OTHER);
  const atWishCap = wishes.length >= FEATURE_WISH_MAX;
  const canSubmit = overall >= 1 && clarity >= 1 && intent !== '' && !mutation.isPending;

  const toggleWish = (label: string) => {
    setWishes((prev) => {
      if (prev.includes(label)) {
        if (label === FEATURE_WISH_OTHER) setFeatureOther('');
        return prev.filter((w) => w !== label);
      }
      // Enforce the "בחר עד 3" cap — ignore selecting a new option past the limit.
      if (prev.length >= FEATURE_WISH_MAX) return prev;
      return [...prev, label];
    });
  };

  const handleSubmit = () => {
    if (intent === '') return;
    const input: FeedbackInput = {
      overall_rating: overall,
      scoring_clarity: clarity,
      return_intent: intent,
      return_blocker: showBlocker && blocker.trim() ? blocker.trim() : null,
      liked_text: liked.trim() || null,
      improve_text: improve.trim() || null,
      feature_wishes: wishes,
      feature_other: showFeatureOther && featureOther.trim() ? featureOther.trim() : null,
      top_improvement: topImprovement.trim() || null,
      other_text: other.trim() || null,
    };
    mutation.mutate(input);
  };

  const pageChrome = (children: React.ReactNode) => (
    <Layout>
      <div className="font-leon min-h-screen bg-[#15191E] text-[#FEFEFE]" dir="rtl">
        <div className="container mx-auto px-4 py-10 max-w-2xl">{children}</div>
      </div>
    </Layout>
  );

  if (loadingExisting) {
    return pageChrome(
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>,
    );
  }

  // Post-submit thank-you + donation CTA.
  if (done) {
    return pageChrome(
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="text-center py-10 space-y-6"
      >
        <div className="text-5xl">🙏</div>
        <h1 className="text-3xl font-bold tracking-tight">תודה על המשוב!</h1>
        <p className="text-white/70 leading-relaxed max-w-md mx-auto">
          כל מילה עוזרת לי לבנות גרסה טובה יותר לטורניר הבא.
        </p>
        <div className="flex flex-col items-center gap-3 pt-2">
          <Link to="/tip-jar">
            <Button variant="accent" size="lg" className="min-h-[48px] gap-2">
              ☕ תמכו בנו
            </Button>
          </Link>
          <Button
            variant="secondary"
            size="lg"
            className="min-h-[48px]"
            onClick={() => setDone(false)}
          >
            ערוך תשובות
          </Button>
        </div>
      </motion.div>,
    );
  }

  return pageChrome(
    <>
      <Link
        to="/"
        className="text-sm text-white/60 hover:text-[#F97116] transition-colors flex items-center gap-1 mb-6 min-h-[44px]"
      >
        <ArrowRight className="w-4 h-4 rotate-180" /> חזרה
      </Link>

      <motion.h1
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="text-3xl font-bold tracking-tight mb-4"
      >
        המשוב שלך
      </motion.h1>
      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="text-white/70 leading-relaxed mb-8"
      >
        תודה שהשתתפת ב-Bracket Run בפלייאוף 2026! שתי דקות מזמנך יעזרו לנו לבנות גרסה טובה יותר לטורניר הבא.
      </motion.p>

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="space-y-5"
      >
        {/* Q1 — overall experience */}
        <QuestionCard title="איך הייתה החוויה הכללית?" required>
          <RatingScale value={overall} onChange={setOverall} />
        </QuestionCard>

        {/* Q2 — scoring clarity */}
        <QuestionCard title="כמה ברורה הייתה שיטת הניקוד מבוססת יחסי ההימורים?" required>
          <RatingScale value={clarity} onChange={setClarity} />
        </QuestionCard>

        {/* Q3 — return intent + conditional blocker */}
        <QuestionCard title="תשתתף שוב בטורניר הבא?" required>
          <div className="grid grid-cols-2 gap-2">
            {RETURN_INTENT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setIntent(opt.value)}
                className={chipClass(intent === opt.value)}
                aria-pressed={intent === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {showBlocker && (
            <div className="mt-4">
              <label htmlFor="feedback-blocker" className="block text-sm text-white/70 mb-2">
                מה היה גורם לך כן לחזור?
              </label>
              <Textarea
                id="feedback-blocker"
                value={blocker}
                onChange={(e) => setBlocker(e.target.value)}
                className={cn(inputClass, 'min-h-[88px]')}
                dir="rtl"
              />
            </div>
          )}
        </QuestionCard>

        {/* Q4 — what worked well */}
        <QuestionCard title="מה עבד טוב במיוחד?">
          <Textarea
            value={liked}
            onChange={(e) => setLiked(e.target.value)}
            className={cn(inputClass, 'min-h-[100px]')}
            dir="rtl"
          />
        </QuestionCard>

        {/* Q5 — what bothered / was missing */}
        <QuestionCard title="מה הכי הפריע או היה חסר?">
          <Textarea
            value={improve}
            onChange={(e) => setImprove(e.target.value)}
            className={cn(inputClass, 'min-h-[100px]')}
            dir="rtl"
          />
        </QuestionCard>

        {/* Q6 — feature wishes (max 3) + conditional "other" */}
        <QuestionCard title="מה הכי ישפר את החוויה בטורניר הבא?">
          <p className="text-sm text-white/60 mb-3">בחר עד 3:</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {FEATURE_WISH_OPTIONS.map((label) => {
              const selected = wishes.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => toggleWish(label)}
                  disabled={atWishCap && !selected}
                  className={chipClass(selected)}
                  aria-pressed={selected}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {showFeatureOther && (
            <div className="mt-4">
              <Input
                value={featureOther}
                onChange={(e) => setFeatureOther(e.target.value)}
                placeholder="פרט/י"
                aria-label="פירוט פיצ'ר אחר"
                className={cn(inputClass, 'min-h-[48px]')}
                dir="rtl"
              />
            </div>
          )}
        </QuestionCard>

        {/* Q7 — single most important improvement */}
        <QuestionCard title="אם היית צריך לבחור רק שיפור אחד — מה הכי חשוב לך?">
          <Input
            value={topImprovement}
            onChange={(e) => setTopImprovement(e.target.value)}
            placeholder="טקסט קצר (חופשי)"
            aria-label="השיפור הכי חשוב"
            className={cn(inputClass, 'min-h-[48px]')}
            dir="rtl"
          />
        </QuestionCard>

        {/* Q8 — anything else */}
        <QuestionCard title="משהו נוסף שבא לך להגיד?">
          <Textarea
            value={other}
            onChange={(e) => setOther(e.target.value)}
            className={cn(inputClass, 'min-h-[100px]')}
            dir="rtl"
          />
        </QuestionCard>

        <Button
          variant="accent"
          size="lg"
          className="w-full min-h-[52px] text-base"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {mutation.isPending ? 'שולח...' : isEditing ? 'עדכן תשובות' : 'שליחה'}
        </Button>
      </motion.div>
    </>,
  );
};

export default FeedbackPage;
